/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';

/**
 * External dependencies
 */
import { useState, useRef, useEffect } from 'react';

export interface FilterOptions {
	employmentType: string;
	location: string;
	datePosted: string;
}

interface FilterDropdownProps {
	filters: FilterOptions;
	onFilterChange: ( key: keyof FilterOptions, value: string ) => void;
	onClearAll: () => void;
	availableOptions: {
		employmentTypes: string[];
		locations: string[];
	};
}

export default function FilterDropdown( { filters, onFilterChange, onClearAll, availableOptions }: FilterDropdownProps ) {
	const [
		isOpen,
		setIsOpen,
	] = useState( false );
	const dropdownRef = useRef< HTMLDivElement >( null );

	const activeFilterCount = Object.values( filters ).filter( ( v ) => v !== '' ).length;

	useEffect( () => {
		function handleClickOutside( event: MouseEvent ) {
			if ( dropdownRef.current && ! dropdownRef.current.contains( event.target as Node ) ) {
				setIsOpen( false );
			}
		}

		if ( isOpen ) {
			document.addEventListener( 'mousedown', handleClickOutside );
		}

		return () => {
			document.removeEventListener( 'mousedown', handleClickOutside );
		};
	}, [ isOpen ] );

	return (
		<div className="filter-dropdown" ref={ dropdownRef }>
			<button
				className={ `filter-dropdown__trigger ${ activeFilterCount > 0 ? 'filter-dropdown__trigger--active' : '' }` }
				onClick={ () => setIsOpen( ! isOpen ) }
				aria-expanded={ isOpen }
				aria-label={ __( 'Filter jobs', 'pca' ) }
			>
				<span>{ __( 'Filter', 'pca' ) }</span>
				{ activeFilterCount > 0 && <span className="filter-dropdown__badge">{ activeFilterCount }</span> }
			</button>

			{ isOpen && (
				<div className="filter-dropdown__panel">
					<div className="filter-dropdown__header">
						<h3 className="filter-dropdown__title">{ __( 'Filters', 'pca' ) }</h3>
						{ activeFilterCount > 0 && (
							<button className="filter-dropdown__clear" onClick={ onClearAll } type="button">
								{ __( 'Clear All', 'pca' ) }
							</button>
						) }
					</div>

					<div className="filter-dropdown__content">
						{ availableOptions.employmentTypes.length > 0 && (
							<div className="filter-dropdown__group">
								<label className="filter-dropdown__label" htmlFor="filter-employment-type">
									{ __( 'Employment Type', 'pca' ) }
								</label>
								<select
									id="filter-employment-type"
									className="filter-dropdown__select"
									value={ filters.employmentType }
									onChange={ ( e ) => onFilterChange( 'employmentType', e.target.value ) }
								>
									<option value="">{ __( 'All Types', 'pca' ) }</option>
									{ availableOptions.employmentTypes.map( ( type ) => (
										<option key={ type } value={ type }>
											{ type }
										</option>
									) ) }
								</select>
							</div>
						) }

						{ availableOptions.locations.length > 0 && (
							<div className="filter-dropdown__group">
								<label className="filter-dropdown__label" htmlFor="filter-location">
									{ __( 'Location', 'pca' ) }
								</label>
								<select
									id="filter-location"
									className="filter-dropdown__select"
									value={ filters.location }
									onChange={ ( e ) => onFilterChange( 'location', e.target.value ) }
								>
									<option value="">{ __( 'All Locations', 'pca' ) }</option>
									{ availableOptions.locations.map( ( loc ) => (
										<option key={ loc } value={ loc }>
											{ loc }
										</option>
									) ) }
								</select>
							</div>
						) }

						<div className="filter-dropdown__group">
							<label className="filter-dropdown__label" htmlFor="filter-date-posted">
								{ __( 'Date Posted', 'pca' ) }
							</label>
							<select
								id="filter-date-posted"
								className="filter-dropdown__select"
								value={ filters.datePosted }
								onChange={ ( e ) => onFilterChange( 'datePosted', e.target.value ) }
							>
								<option value="">{ __( 'All Time', 'pca' ) }</option>
								<option value="7">{ __( 'Last 7 Days', 'pca' ) }</option>
								<option value="30">{ __( 'Last 30 Days', 'pca' ) }</option>
								<option value="90">{ __( 'Last 90 Days', 'pca' ) }</option>
							</select>
						</div>
					</div>
				</div>
			) }
		</div>
	);
}
